import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import partnerService from '../../services/partnerService';

const PartnerManagement = () => {
  const [partners, setPartners] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    loadPartners();
  }, []);

  const loadPartners = async () => {
    try {
      // Fetch partners from Admin_Marketing_Exec_Data via API
      const response = await fetch('/api/partners');
      if (!response.ok) {
        throw new Error('Failed to fetch partners');
      }
      const partnersData = await response.json();
      setPartners(partnersData.partners || []);
    } catch (error) {
      console.error('Failed to load partners:', error);
      // Set empty array on error
      setPartners([]);
    }
  };

  const partnerStats = [
    { name: 'Referral Partners', value: partners.filter(p => p.type === 'Referral Partner').length },
    { name: 'Service Partners', value: partners.filter(p => p.type === 'Service Delivery Partner').length },
    { name: 'Total Commissions', value: partners.reduce((sum, p) => sum + p.commissions, 0) },
    { name: 'Active Partners', value: partners.filter(p => p.status === 'Active').length }
  ];

  const growthData = [
    { stage: '10 Clients', partners: 2 },
    { stage: '100 Clients', partners: 3 },
    { stage: '1,000 Clients', partners: 1 },
    { stage: '10,000 Clients', partners: 0 }
  ];

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-8">
      <div className="text-center space-y-4">
        <h1 className="text-4xl font-bold text-gray-900">Partner Management Dashboard</h1>
        <p className="text-xl text-gray-600">Manage referral partners, service delivery partners, and BizBuilding Program progress</p>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {partnerStats.map((stat, index) => (
          <Card key={index}>
            <CardContent className="p-6">
              <div className="text-2xl font-bold text-blue-600">{stat.value}</div>
              <div className="text-sm text-gray-600">{stat.name}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* BizBuilding Growth Chart */}
      <Card>
        <CardHeader>
          <CardTitle>BizBuilding Program Growth Distribution</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={growthData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="stage" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="partners" fill="#3b82f6" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="overview">Partner Overview</TabsTrigger>
          <TabsTrigger value="management">Partner Management</TabsTrigger>
          <TabsTrigger value="bizbuilding">BizBuilding Program</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>All Partners</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Commissions</TableHead>
                    <TableHead>BizBuilding Stage</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {partners.map((partner) => (
                    <TableRow key={partner.id}>
                      <TableCell>{partner.name}</TableCell>
                      <TableCell>
                        <Badge variant={partner.type === 'Referral Partner' ? 'default' : 'secondary'}>
                          {partner.type}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={partner.status === 'Active' ? 'default' : 'secondary'}>
                          {partner.status}
                        </Badge>
                      </TableCell>
                      <TableCell>R{partner.commissions}</TableCell>
                      <TableCell>{partner.bizBuildingStage}</TableCell>
                      <TableCell>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSelectedPartner(partner)}
                        >
                          View Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="management" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Partner Registration</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Partner Type</label>
                  <select className="w-full p-2 border border-gray-300 rounded-md">
                    <option value="">Select Type</option>
                    <option value="referral">Referral Partner</option>
                    <option value="service">Service Delivery Partner</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Partner Name</label>
                  <Input placeholder="Full Name" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Email</label>
                  <Input type="email" placeholder="email@example.com" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Phone</label>
                  <Input placeholder="+27 XX XXX XXXX" />
                </div>
              </div>
              <Button>Register Partner</Button>
            </CardContent>
          </Card>

          {selectedPartner && (
            <Card>
              <CardHeader>
                <CardTitle>Partner Details: {selectedPartner.name}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Type</label>
                    <Badge variant={selectedPartner.type === 'Referral Partner' ? 'default' : 'secondary'}>
                      {selectedPartner.type}
                    </Badge>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Status</label>
                    <Badge variant={selectedPartner.status === 'Active' ? 'default' : 'secondary'}>
                      {selectedPartner.status}
                    </Badge>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Total Commissions</label>
                    <div className="text-lg font-semibold">R{selectedPartner.commissions}</div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">BizBuilding Stage</label>
                    <div className="text-lg font-semibold">{selectedPartner.bizBuildingStage}</div>
                  </div>
                </div>

                {selectedPartner.type === 'Referral Partner' && (
                  <div>
                    <label className="block text-sm font-medium mb-2">Successful Referrals</label>
                    <div className="text-lg font-semibold">{selectedPartner.referrals}</div>
                  </div>
                )}

                {selectedPartner.type === 'Service Delivery Partner' && (
                  <div>
                    <label className="block text-sm font-medium mb-2">Services Delivered</label>
                    <div className="text-lg font-semibold">{selectedPartner.servicesDelivered}</div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="bizbuilding" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>BizBuilding Program Management</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl font-bold text-blue-600">2</div>
                    <div className="text-sm text-gray-600">10 Client Milestone</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl font-bold text-green-600">3</div>
                    <div className="text-sm text-gray-600">100 Client Milestone</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl font-bold text-purple-600">1</div>
                    <div className="text-sm text-gray-600">1,000 Client Milestone</div>
                  </CardContent>
                </Card>
              </div>

              <div>
                <h3 className="text-lg font-medium mb-4">Growth Milestone Achievements</h3>
                <div className="space-y-2">
                  {partners.map((partner) => (
                    <div key={partner.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <div className="font-medium">{partner.name}</div>
                        <div className="text-sm text-gray-600">Current: {partner.bizBuildingStage}</div>
                      </div>
                      <Button variant="outline" size="sm">
                        Update Progress
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PartnerManagement;